// Internal Audit — lean module: plan · manual sample selection · findings · report. Admin/QM only.
// Reuses internal_corrective_actions for corrective actions (no new CA system). No source workflow is modified.
import React, { useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { isOtherService } from '../lib/service'
import { useAuth } from '../lib/AuthContext'
import { format, parseISO } from 'date-fns'
import { IA_CHECKLIST, IA_SECTIONS } from '../lib/internalAuditChecklist'
import { toast, failed } from '../lib/toast'

const STATUSES = ['Planned', 'In Progress', 'Reported', 'Closed']
const STANDARDS = ['CT17', 'ISO 17021', 'SAC checklist', 'Internal procedure']
const FINDING_TYPES = ['Major NC', 'Minor NC', 'Observation', 'OFI']
const CONFORMITY = ['', 'Conforms', 'NC', 'Observation', 'N/A']
// Legend mirrors the SAC CT 17 checklist: C · O · T · N · N/A · F
const IA_RESP = ['', 'Complies', 'Observation', 'To address at audit', 'Nonconformity', 'N/A', 'Further info required']
const IA_RESP_COLOR = { Complies: '#27500a', Observation: '#854F0B', 'To address at audit': '#1F3864', Nonconformity: '#791F1F', 'N/A': '#666', 'Further info required': '#4527A0' }

// Standing default text for Objective / Scope / Criteria — pre-filled on every NEW internal audit so the same
// wording carries over each cycle and is simply amended if needed (not re-typed from scratch).
const IA_DEFAULTS = {
  objective: "To verify that CCIS's management system for its WSH auditing activities is established, implemented and maintained in conformance with SAC CT 17, ISO/IEC 17021-1 and CCIS's Management & Procedures Manual, and to confirm its continued effectiveness and readiness for SAC assessment.",
  scope: "The CCIS management system for bizSAFE RM, SHMS and ConSASS auditing activities — impartiality and independence, competence and personnel, application review and contracting, audit planning, conduct, reporting and decision-making, records, complaints, and management review — sampled across audit jobs conducted in the audit period.",
  criteria: "SAC CT 17 (incl. Appendices 1 & 2), ISO/IEC 17021-1, the applicable SAC assessor checklist, and CCIS's Management & Procedures Manual (MP-01).",
}

// Draft a defensible conclusion from the recorded results (samples · findings · checklist). Editable afterwards.
function draftConclusion(a, samples, findings) {
  const schemes = Array.from(new Set((samples || []).map(s => (s.snapshot || {}).audit_type).filter(Boolean)))
  const majors = (findings || []).filter(f => (f.finding_type || '').includes('Major')).length
  const minors = (findings || []).filter(f => (f.finding_type || '') === 'Minor NC').length
  const obs = (findings || []).filter(f => (f.finding_type || '') === 'Observation').length
  const ofis = (findings || []).filter(f => (f.finding_type || '') === 'OFI').length
  const openNC = (findings || []).filter(f => (f.finding_type || '').includes('NC') && f.status !== 'Closed').length
  const cl = a.checklist || {}
  const answered = IA_CHECKLIST.filter(it => cl[it.id]?.r).length
  const ncCount = majors + minors
  const parts = []
  parts.push(`This internal audit examined ${samples?.length || 0} sample${(samples?.length || 0) === 1 ? '' : 's'}${schemes.length ? ` across ${schemes.join(', ')}` : ''} and assessed the management system against SAC CT 17, ISO/IEC 17021-1 and CCIS's Management & Procedures Manual (${answered} of ${IA_CHECKLIST.length} checklist items assessed).`)
  if (ncCount === 0) parts.push(`No nonconformities were raised${obs || ofis ? `; ${[obs ? `${obs} observation${obs === 1 ? '' : 's'}` : '', ofis ? `${ofis} opportunity for improvement${ofis === 1 ? '' : 's'}` : ''].filter(Boolean).join(' and ')} were noted` : ''}.`)
  else parts.push(`${[majors ? `${majors} major` : '', minors ? `${minors} minor` : ''].filter(Boolean).join(' and ')} nonconformit${ncCount === 1 ? 'y was' : 'ies were'} raised${obs || ofis ? `, together with ${[obs ? `${obs} observation${obs === 1 ? '' : 's'}` : '', ofis ? `${ofis} OFI${ofis === 1 ? '' : 's'}` : ''].filter(Boolean).join(' and ')}` : ''}.`)
  if (ncCount === 0) parts.push('The management system is assessed as effectively implemented and maintained.')
  else parts.push(`The management system is assessed as effective overall, subject to closure of the corrective action${ncCount === 1 ? '' : 's'} raised${openNC ? ` (${openNC} still open)` : ''}.`)
  parts.push('Results are carried forward as an input to the next Management Review.')
  return parts.join(' ')
}

function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }
function nextRef(rows) {
  const yr = new Date().getFullYear()
  let max = 0
  for (const r of rows || []) { const m = new RegExp(`^IA-${yr}-(\\d+)$`).exec(r.ref || ''); if (m) max = Math.max(max, +m[1]) }
  return `IA-${yr}-${String(max + 1).padStart(2, '0')}`
}

export default function InternalAudit() {
  const { isAuditor } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState(null)

  useEffect(() => { if (!isAuditor) load() }, [isAuditor])
  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('internal_audits').select('*').order('created_at', { ascending: false })
    if (error) console.warn('internal_audits load error (run INTERNAL_AUDIT.sql):', error.message)
    setRows(data || []); setLoading(false)
  }

  if (isAuditor) return (
    <div><div className="page-header"><h1>CCIS Internal Audit</h1></div>
      <div className="notice notice-info" style={{ fontSize: 13 }}>This page is not available for the Auditor role.</div></div>
  )

  if (openId) return <InternalAuditDetail id={openId} onBack={() => { setOpenId(null); load() }} allRows={rows} />

  return (
    <div>
      <div className="page-header">
        <h1>CCIS Internal Audit</h1>
        <CreateButton allRows={rows} onCreated={(id) => { load(); setOpenId(id) }} />
      </div>
      <div className="notice notice-info" style={{ fontSize: 13, marginBottom: 14 }}>
        Plan an internal audit, manually select samples after filtering, record checklist findings against CT17 / ISO 17021 / SAC criteria, link corrective actions, and print the report.
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Ref</th><th>Title</th><th>Scope</th><th>Period</th><th>Planned</th><th>Status</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No internal audits yet.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpenId(r.id)}>
                  <td style={{ fontWeight: 500 }}>{r.ref || '—'}</td>
                  <td style={{ fontSize: 13 }}>{r.title || '—'}</td>
                  <td style={{ fontSize: 13, maxWidth: 240 }}>{r.scope || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.period_start)} – {fmt(r.period_end)}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.planned_date)}</td>
                  <td><span className={`badge ${r.status === 'Closed' || r.status === 'Reported' ? 'badge-ok' : r.status === 'In Progress' ? 'badge-info' : 'badge-warn'}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function CreateButton({ allRows, onCreated }) {
  const { profile, canApprove, logAction } = useAuth()
  const [busy, setBusy] = useState(false)
  if (!canApprove) return null
  async function create() {
    setBusy(true)
    const ref = nextRef(allRows)
    const { data, error } = await supabase.from('internal_audits')
      .insert({ ref, title: 'Internal audit ' + ref, status: 'Planned', created_by: profile?.name || 'Staff',
        objective: IA_DEFAULTS.objective, scope: IA_DEFAULTS.scope, criteria: IA_DEFAULTS.criteria }).select().single()
    setBusy(false)
    if (error) { failed("Couldn't create the internal audit", error.message); return }
    if (logAction) logAction('Internal audit created', 'internal_audits', data.id, ref, {})
    onCreated(data.id)
  }
  return <button className="primary" disabled={busy} onClick={create}>+ New internal audit</button>
}

// ───────────────────────────── Detail (details · samples · findings · report) ─────────────────────────────
function InternalAuditDetail({ id, onBack }) {
  const { profile, canApprove, logAction } = useAuth()
  const [a, setA] = useState(null)
  const [samples, setSamples] = useState([])
  const [findings, setFindings] = useState([])
  const [cas, setCas] = useState([])
  const [picker, setPicker] = useState(false)
  const [findForm, setFindForm] = useState(null)
  const [manualForm, setManualForm] = useState(null)   // manual (non-AuditPM) sample, e.g. a ConSASS/SHMS audit
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [id])
  async function load() {
    const [{ data: au }, { data: sa }, { data: fi }, { data: ca }] = await Promise.all([
      supabase.from('internal_audits').select('*').eq('id', id).single(),
      supabase.from('internal_audit_samples').select('*').eq('internal_audit_id', id).order('created_at'),
      supabase.from('internal_audit_findings').select('*').eq('internal_audit_id', id).order('created_at'),
      supabase.from('internal_corrective_actions').select('id, issue, status, source').order('created_at', { ascending: false }),
    ])
    setA(au || null); setSamples(sa || []); setFindings(fi || []); setCas(ca || [])
  }
  const set = (k, v) => setA(p => ({ ...p, [k]: v }))
  // Fill any BLANK Objective / Scope / Criteria with the standing default wording (never overwrites text present).
  const fillSuggested = () => setA(p => ({
    ...p,
    objective: (p.objective || '').trim() ? p.objective : IA_DEFAULTS.objective,
    scope: (p.scope || '').trim() ? p.scope : IA_DEFAULTS.scope,
    criteria: (p.criteria || '').trim() ? p.criteria : IA_DEFAULTS.criteria,
  }))

  async function saveDetails() {
    setSaving(true)
    const prevStatus = (await supabase.from('internal_audits').select('status').eq('id', id).single()).data?.status
    const payload = {
      ref: a.ref, title: a.title, scope: a.scope, criteria: a.criteria, objective: a.objective, conclusion: a.conclusion,
      period_start: a.period_start || null, period_end: a.period_end || null,
      planned_date: a.planned_date || null, conducted_date: a.conducted_date || null,
      lead_internal_auditor: a.lead_internal_auditor, status: a.status || 'Planned', updated_at: new Date().toISOString(),
    }
    const { error } = await supabase.from('internal_audits').update(payload).eq('id', id)
    setSaving(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) {
      logAction('Internal audit updated', 'internal_audits', id, a.ref, {})
      if (prevStatus !== payload.status && (payload.status === 'Reported' || payload.status === 'Closed'))
        logAction(`Internal audit ${payload.status.toLowerCase()}`, 'internal_audits', id, a.ref, { status: payload.status })
    }
    load()
  }

  async function addSamples(items) {
    const payload = items.map(it => ({ internal_audit_id: id, source_table: it.source_table, source_id: it.source_id, reference_label: it.reference_label, snapshot: it.snapshot, conformity: '' }))
    const { error } = await supabase.from('internal_audit_samples').insert(payload)
    if (error) { failed("Couldn't add the samples", error.message); return }
    if (logAction) logAction('Internal audit samples added', 'internal_audits', id, a?.ref, { count: items.length })
    setPicker(false); load()
  }
  async function updateSample(s, patch) {
    await supabase.from('internal_audit_samples').update(patch).eq('id', s.id); load()
  }
  async function removeSample(s) {
    if (!window.confirm('Remove this sample?')) return
    await supabase.from('internal_audit_samples').delete().eq('id', s.id)
    if (logAction) logAction('Internal audit sample removed', 'internal_audits', id, a?.ref, { ref: s.reference_label })
    load()
  }
  // Manual sample: an audit NOT held in AuditPM (e.g. a ConSASS / SHMS audit recorded outside the RM system).
  async function saveManualSample() {
    const f = manualForm || {}
    const ref = (f.reference_label || '').trim()
    if (!ref) { toast('Enter a reference or description', 'warning'); return }
    await addSamples([{
      source_table: 'manual', source_id: null, reference_label: ref,
      snapshot: { audit_type: (f.audit_type || '').trim() || null, audit_date: (f.audit_date || '').trim() || null,
        auditor: (f.auditor || '').trim() || null, decision_maker: (f.decision_maker || '').trim() || null, manual: true },
    }])
    setManualForm(null)
  }
  async function deleteAudit() {
    if (!window.confirm(`Delete internal audit "${a.ref}"?\n\nThis permanently removes the audit and all its samples and findings. This cannot be undone.`)) return
    const { error } = await supabase.from('internal_audits').delete().eq('id', id)
    if (error) { failed("Couldn't delete", error.message); return }
    if (logAction) logAction('Internal audit deleted', 'internal_audits', id, a.ref, {})
    onBack && onBack()
  }
  async function removeFinding(f) {
    if (!window.confirm('Delete this finding?')) return
    await supabase.from('internal_audit_findings').delete().eq('id', f.id); load()
  }
  async function toggleFindingClosed(f) {
    const ns = f.status === 'Closed' ? 'Open' : 'Closed'
    await supabase.from('internal_audit_findings').update({ status: ns, updated_at: new Date().toISOString() }).eq('id', f.id)
    if (logAction) logAction(`Internal audit finding ${ns === 'Closed' ? 'closed' : 'reopened'}`, 'internal_audit_findings', f.id, a?.ref, {})
    load()
  }
  // Marking a checklist item Observation/Nonconformity only saves onto the audit's own checklist blob —
  // it does NOT create a Finding, so Management Review / the Assessment Portal (which both only read
  // internal_audit_findings) never see it. This turns any not-yet-synced checklist tags into real Finding
  // rows in one click. 'To address at audit' / 'Further info required' are left out — those are working
  // notes, not formal findings. Nonconformity defaults to Minor NC; escalate to Major NC by hand if needed.
  // checklist_item_id makes this idempotent — re-running it only creates rows for items not already synced.
  const [syncing, setSyncing] = useState(false)
  async function syncFromChecklist() {
    const cl = a.checklist || {}
    const RESP_MAP = { Observation: 'Observation', Nonconformity: 'Minor NC' }
    const already = new Set(findings.map(f => f.checklist_item_id).filter(Boolean))
    const toCreate = IA_CHECKLIST.filter(it => { const r = cl[it.id]?.r; return r && RESP_MAP[r] && !already.has(it.id) })
    if (toCreate.length === 0) { toast('Nothing new to sync', 'info', { detail: 'All Observations and NCs are already in Findings' }); return }
    if (!window.confirm(`Create ${toCreate.length} finding${toCreate.length === 1 ? '' : 's'} from checklist items marked Observation / Nonconformity?`)) return
    setSyncing(true)
    const payload = toCreate.map(it => ({
      internal_audit_id: id, checklist_item_id: it.id, standard: 'CT17', clause_ref: it.cl,
      finding_type: RESP_MAP[cl[it.id].r], description: it.q, evidence: cl[it.id]?.e || null, status: 'Open',
    }))
    const { error } = await supabase.from('internal_audit_findings').insert(payload)
    setSyncing(false)
    if (error) { failed("Couldn't sync", error.message); return }
    if (logAction) logAction('Internal audit findings synced from checklist', 'internal_audit_findings', id, a?.ref, { count: toCreate.length })
    load()
  }

  if (!a) return <div style={{ padding: 20, color: 'var(--text3)' }}>Loading…</div>
  const ro = !canApprove   // read-only for non-Admin/QM office viewers

  return (
    <div>
      <div className="page-header">
        <h1>{a.ref} — {a.title || 'Internal audit'}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="primary" onClick={() => exportReport(a, samples, findings, cas)}>⬇ Export report (Word)</button>
          <button onClick={() => printReport(a, samples, findings, cas)}>Print / PDF</button>
          {!ro && <button className="danger" onClick={deleteAudit}>Delete</button>}
          <button onClick={onBack}>← Back</button>
        </div>
      </div>

      {/* Details */}
      <div className="card" style={{ marginBottom: 12 }}>
        <h2 className="card-title">Audit details</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div className="field"><label>Reference</label><input value={a.ref || ''} disabled={ro} onChange={e => set('ref', e.target.value)} /></div>
          <div className="field"><label>Status</label><select value={a.status || 'Planned'} disabled={ro} onChange={e => set('status', e.target.value)}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Title</label><input value={a.title || ''} disabled={ro} onChange={e => set('title', e.target.value)} /></div>
          <div className="field"><label>Lead internal auditor</label><input value={a.lead_internal_auditor || ''} disabled={ro} onChange={e => set('lead_internal_auditor', e.target.value)} /></div>
          <div className="field"><label>Planned date</label><input type="date" value={a.planned_date || ''} disabled={ro} onChange={e => set('planned_date', e.target.value)} /></div>
          <div className="field"><label>Conducted date</label><input type="date" value={a.conducted_date || ''} disabled={ro} onChange={e => set('conducted_date', e.target.value)} /></div>
          <div className="field"><label>Period start</label><input type="date" value={a.period_start || ''} disabled={ro} onChange={e => set('period_start', e.target.value)} /></div>
          <div className="field"><label>Period end</label><input type="date" value={a.period_end || ''} disabled={ro} onChange={e => set('period_end', e.target.value)} /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Objective</label><textarea rows={2} value={a.objective || ''} disabled={ro} onChange={e => set('objective', e.target.value)} /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Scope</label><textarea rows={2} value={a.scope || ''} disabled={ro} onChange={e => set('scope', e.target.value)} placeholder="e.g. WSH RM audit process, report approvals, impartiality, competence" /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Criteria</label><textarea rows={2} value={a.criteria || ''} disabled={ro} onChange={e => set('criteria', e.target.value)} placeholder="e.g. SAC CT17, ISO/IEC 17021-1, SAC checklist, internal M&P" /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <label>Conclusion</label>
              {!ro && <button type="button" className="sm" onClick={() => set('conclusion', draftConclusion(a, samples, findings))}>↻ Draft from results</button>}
            </div>
            <textarea rows={3} value={a.conclusion || ''} disabled={ro} onChange={e => set('conclusion', e.target.value)} placeholder="Type, or click “Draft from results” to generate from the samples, findings and checklist — then amend." />
          </div>
        </div>
        {!ro && <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="primary" onClick={saveDetails} disabled={saving}>{saving ? 'Saving…' : 'Save details'}</button>
          <button type="button" className="sm" onClick={fillSuggested}>Insert suggested Objective / Scope / Criteria</button>
        </div>}
      </div>

      {/* Samples */}
      <div className="card" style={{ marginBottom: 12, padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px' }}>
          <h2 className="card-title">Selected samples ({samples.length})</h2>
          {!ro && <div style={{ display: 'flex', gap: 8 }}>
            <button className="sm" onClick={() => setManualForm({ audit_type: 'ConSASS Audit' })}>+ Add manual sample</button>
            <button className="sm primary" onClick={() => setPicker(true)}>+ Select samples</button>
          </div>}
        </div>
        {!ro && manualForm && (
          <div style={{ padding: '0 14px 12px' }}>
            <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Add a sample not held in AuditPM (e.g. a ConSASS / SHMS audit)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
                <div className="field" style={{ gridColumn: '1 / -1' }}><label>Reference / description *</label><input value={manualForm.reference_label || ''} onChange={e => setManualForm(f => ({ ...f, reference_label: e.target.value }))} placeholder="e.g. KTC CR 206 — ConSASS (14–15 Jul 2026)" /></div>
                <div className="field"><label>Scheme</label><input value={manualForm.audit_type || ''} onChange={e => setManualForm(f => ({ ...f, audit_type: e.target.value }))} placeholder="ConSASS Audit" /></div>
                <div className="field"><label>Audit date(s)</label><input value={manualForm.audit_date || ''} onChange={e => setManualForm(f => ({ ...f, audit_date: e.target.value }))} placeholder="14–15 Jul 2026" /></div>
                <div className="field"><label>Auditor(s)</label><input value={manualForm.auditor || ''} onChange={e => setManualForm(f => ({ ...f, auditor: e.target.value }))} /></div>
                <div className="field"><label>Report approver / reviewer</label><input value={manualForm.decision_maker || ''} onChange={e => setManualForm(f => ({ ...f, decision_maker: e.target.value }))} /></div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button className="sm primary" onClick={saveManualSample}>Add sample</button>
                <button className="sm" onClick={() => setManualForm(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        <div className="table-wrap">
          <table>
            <thead><tr><th>Reference</th><th>Key facts</th><th>Conformity</th><th>Notes</th>{!ro && <th></th>}</tr></thead>
            <tbody>
              {samples.length === 0 && <tr><td colSpan={ro ? 4 : 5} style={{ color: 'var(--text3)', textAlign: 'center', padding: 18 }}>No samples selected.</td></tr>}
              {samples.map(s => {
                const sn = s.snapshot || {}
                return (
                  <tr key={s.id}>
                    <td style={{ fontSize: 13, maxWidth: 220 }}>{s.reference_label}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 280 }}>
                      {sn.audit_type || '—'} · v{sn.report_version ?? '—'} · DM {sn.decision_maker || '—'}
                      {sn.coi_flagged ? ' · ⚠COI' : ''}{sn.complaints ? ` · ${sn.complaints} complaint(s)` : ''}
                    </td>
                    <td>{ro ? (s.conformity || '—') : (
                      <select value={s.conformity || ''} onChange={e => updateSample(s, { conformity: e.target.value })}>{CONFORMITY.map(c => <option key={c} value={c}>{c || '—'}</option>)}</select>
                    )}</td>
                    <td>{ro ? (s.sample_notes || '—') : (
                      <input value={s.sample_notes || ''} placeholder="evidence notes" onChange={e => setSamples(list => list.map(x => x.id === s.id ? { ...x, sample_notes: e.target.value } : x))} onBlur={e => updateSample(s, { sample_notes: e.target.value })} style={{ minWidth: 160 }} />
                    )}</td>
                    {!ro && <td><button className="sm danger" onClick={() => removeSample(s)}>Remove</button></td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Findings */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px' }}>
          <h2 className="card-title">Findings &amp; observations ({findings.length})</h2>
          {!ro && <div style={{ display: 'flex', gap: 8 }}>
            <button className="sm" disabled={syncing} onClick={syncFromChecklist} title="Create Finding entries for any checklist item marked Observation/Nonconformity that isn't in this list yet">{syncing ? 'Syncing…' : 'Sync from checklist'}</button>
            <button className="sm primary" onClick={() => setFindForm({ standard: 'CT17', finding_type: 'Observation', status: 'Open' })}>+ Add finding</button>
          </div>}
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Type</th><th>Standard / clause</th><th>Description</th><th>Sample</th><th>Corrective action</th><th>Status</th>{!ro && <th></th>}</tr></thead>
            <tbody>
              {findings.length === 0 && <tr><td colSpan={ro ? 6 : 7} style={{ color: 'var(--text3)', textAlign: 'center', padding: 18 }}>No findings recorded.</td></tr>}
              {findings.map(f => {
                const ca = cas.find(c => c.id === f.corrective_action_id)
                const samp = samples.find(s => s.id === f.sample_id)
                return (
                  <tr key={f.id}>
                    <td><span className={`badge ${f.finding_type?.includes('NC') ? 'badge-warn' : 'badge'}`}>{f.finding_type}</span></td>
                    <td style={{ fontSize: 13 }}>{f.standard}{f.clause_ref ? ` ${f.clause_ref}` : ''}</td>
                    <td style={{ fontSize: 13, maxWidth: 280 }}>{f.description || '—'}</td>
                    <td style={{ fontSize: 13, color: 'var(--text3)', maxWidth: 150 }}>{samp ? samp.reference_label : '—'}</td>
                    <td style={{ fontSize: 13 }}>{ca ? `${ca.issue?.slice(0, 28) || 'CA'} (${ca.status})` : '—'}</td>
                    <td><span className={`badge ${f.status === 'Closed' ? 'badge-ok' : 'badge-warn'}`}>{f.status}</span></td>
                    {!ro && <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="sm" onClick={() => setFindForm(f)}>Edit</button>{' '}
                      <button className="sm" onClick={() => toggleFindingClosed(f)}>{f.status === 'Closed' ? 'Reopen' : 'Close'}</button>{' '}
                      <button className="sm danger" onClick={() => removeFinding(f)}>×</button>
                    </td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ChecklistCard auditId={id} initial={a.checklist || {}} samples={samples} ro={ro} onSaved={cl => setA(p => ({ ...p, checklist: cl }))} />

      {picker && <SamplePicker existing={samples} onClose={() => setPicker(false)} onAdd={addSamples} />}
      {findForm && <FindingForm auditId={id} auditRef={a.ref} form={findForm} samples={samples} cas={cas} profile={profile} logAction={logAction} onClose={() => setFindForm(null)} onSaved={() => { setFindForm(null); load() }} />}
    </div>
  )
}

// ───────────────────────── Full SAC / CT 17 checklist (per internal audit) ─────────────────────────
const JOBEV_MARK = ' · Sampled jobs: '   // separates the auditor's manual evidence text from the auto job references
function jobEvidenceStr(s) {
  // Clean clause-evidence citation — a reference to the sampled job (whose full facts live in the report's
  // Samples table), not a status dump. reference_label already carries "<client> — <scheme> (<date>)".
  return `Sampled job: ${s.reference_label}`
}
function ChecklistCard({ auditId, initial, samples, ro, onSaved }) {
  const [cl, setCl] = useState(initial || {})
  const [open, setOpen] = useState({})
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const setItem = (id, patch) => { setCl(m => ({ ...m, [id]: { ...(m[id] || {}), ...patch } })); setDirty(true) }
  // Seed a fresh checklist from the pre-filled answers on first open (only when nothing saved yet).
  useEffect(() => {
    if (ro) return
    if (initial && Object.keys(initial).length) return
    setCl(m => {
      const n = { ...m }
      IA_CHECKLIST.forEach(it => { if (!n[it.id]) n[it.id] = { r: it.r || '', e: it.e || '' } })
      return n
    })
    setDirty(true)
  }, [])
  // Auto-fill job evidence: whenever the jobs picked at the top change, stamp their references onto every
  // audit-lifecycle (jobEv) item's evidence automatically — no per-item clicking. Any text the auditor typed
  // BEFORE the marker is preserved; the "· Sampled jobs: …" suffix is refreshed to match the current selection.
  const lastRefs = useRef(null)
  useEffect(() => {
    if (ro) return
    const refs = (samples || []).map(s => s.reference_label).filter(Boolean).join('; ')
    if (lastRefs.current === refs) return
    lastRefs.current = refs
    setCl(m => {
      const n = { ...m }
      IA_CHECKLIST.forEach(it => {
        if (!it.jobEv) return
        const cur = n[it.id] || {}
        const base = String(cur.e ?? it.e ?? '').split(JOBEV_MARK)[0]
        n[it.id] = { ...cur, e: refs ? base + JOBEV_MARK + refs : base }
      })
      return n
    })
    if (refs) setDirty(true)
  }, [samples, ro])
  // Re-apply pre-fills to any blank items without touching answers the auditor has already changed.
  function seedDefaults() {
    setCl(m => {
      const n = { ...m }
      IA_CHECKLIST.forEach(it => {
        const c = n[it.id] || {}
        n[it.id] = { r: c.r || it.r || '', e: c.e || it.e || '' }
      })
      return n
    })
    setDirty(true)
  }
  async function save() {
    setSaving(true)
    const { error } = await supabase.from('internal_audits').update({ checklist: cl, updated_at: new Date().toISOString() }).eq('id', auditId)
    setSaving(false); if (error) { failed("Couldn't save", error.message); return } setDirty(false); onSaved && onSaved(cl)
  }
  const bySection = {}; IA_CHECKLIST.forEach(it => { (bySection[it.s] = bySection[it.s] || []).push(it) })
  const answered = IA_CHECKLIST.filter(it => cl[it.id]?.r).length
  const sightItems = IA_CHECKLIST.filter(it => it.sight)
  const sightDone = sightItems.filter(it => cl[it.id]?.r).length
  const pct = IA_CHECKLIST.length ? Math.round(answered / IA_CHECKLIST.length * 100) : 0
  const RESP_STYLE = { Complies: ['#27500a', '#EAF3DE'], Observation: ['#854F0B', '#FFF3E0'], 'To address at audit': ['#1F3864', '#E7ECF6'], Nonconformity: ['#791F1F', '#FDECEA'], 'N/A': ['#555', '#eef0f2'], 'Further info required': ['#4527A0', '#EDE7F6'] }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
        <div>
          <h2 className="card-title">Checklist — SAC / CT 17</h2>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>{answered} of {IA_CHECKLIST.length} answered · pre-filled from AuditPM controls & the M&P. <b style={{ color: '#854F0B' }}>{sightDone}/{sightItems.length} sight items done</b> — the amber-flagged items still need you to sight a record. For SHMS/ConSASS, add each audit as a manual sample above — it flows into the process items automatically.</div>
        </div>
        {!ro && <div style={{ display: 'flex', gap: 8 }}>
          <button className="sm" onClick={seedDefaults}>Re-apply pre-fills</button>
          <button className="sm primary" onClick={save} disabled={saving || !dirty}>{saving ? 'Saving…' : dirty ? 'Save checklist' : 'Saved'}</button>
        </div>}
      </div>
      <div style={{ height: 6, background: 'var(--bg2)', borderRadius: 6, overflow: 'hidden', margin: '4px 0 10px' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#639922' : 'var(--brand)', transition: 'width .2s' }} />
      </div>
      {Object.entries(IA_SECTIONS).map(([k, label]) => {
        const items = bySection[k] || []; if (!items.length) return null
        const ans = items.filter(it => cl[it.id]?.r).length
        const isOpen = open[k]
        return (
          <div key={k} style={{ borderTop: '1px solid var(--border)' }}>
            <button onClick={() => setOpen(o => ({ ...o, [k]: !o[k] }))} style={{ width: '100%', background: 'none', border: 'none', padding: '10px 2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              <span>{isOpen ? '▾' : '▸'} {label}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: ans === items.length ? '#27500a' : 'var(--text3)' }}>{ans}/{items.length}</span>
            </button>
            {isOpen && items.map(it => {
              const c = cl[it.id] || {}
              const needsSight = it.sight && !c.r
              return (
                <div key={it.id} style={{ padding: '10px 2px 10px 8px', borderTop: '1px dashed var(--border)', borderLeft: needsSight ? '3px solid #E8A33D' : '3px solid transparent', background: needsSight ? '#FFFBF4' : 'transparent' }}>
                  <div style={{ fontSize: 13, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#4527A0', background: '#EDE7F6', padding: '1px 6px', borderRadius: 6, marginRight: 6 }}>{it.cl}</span>
                    {it.sight && <span style={{ fontSize: 13, fontWeight: 700, color: '#854F0B', background: '#FFF3E0', padding: '1px 6px', borderRadius: 6, marginRight: 6, letterSpacing: '.03em' }}>SIGHT</span>}
                    {it.q}
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 7 }}>
                    {['Complies', 'Observation', 'To address at audit', 'Nonconformity', 'N/A', 'Further info required'].map(x => {
                      const on = c.r === x; const [fg, bg] = RESP_STYLE[x]
                      return (
                        <button key={x} disabled={ro} onClick={() => setItem(it.id, { r: on ? '' : x })}
                          style={{ fontSize: 13, padding: '3px 11px', borderRadius: 20, cursor: ro ? 'default' : 'pointer',
                            border: `1px solid ${on ? fg : 'var(--border)'}`, background: on ? bg : 'var(--bg)', color: on ? fg : 'var(--text3)', fontWeight: on ? 600 : 500 }}>{x}</button>
                      )
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                    <textarea rows={2} disabled={ro} value={c.e ?? ''} placeholder={`Evidence — ${it.e}`}
                      onChange={e => setItem(it.id, { e: e.target.value })} style={{ flex: 1, minWidth: 0, fontSize: 13 }} />
                  </div>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

// ───────────────────────────── Finding form (modal) ─────────────────────────────
function FindingForm({ auditId, auditRef, form, samples, cas, profile, logAction, onClose, onSaved }) {
  const [f, setF] = useState(form)
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF(p => ({ ...p, [k]: v }))
  async function save() {
    if (!(f.description || '').trim()) { toast('Enter a description', 'warning'); return }
    setBusy(true)
    let caId = f.corrective_action_id || null
    // Create a corrective action from this finding (reuses internal_corrective_actions, source = Internal audit).
    if (f.corrective_action_id === '__new__') {
      const { data: ca, error: ce } = await supabase.from('internal_corrective_actions')
        .insert({
          source: 'Internal audit', issue: f.description, status: 'Open', created_by: profile?.name || 'Staff',
          grading: f.finding_type || null, clause_ref: f.clause_ref || null, assessment_reference: auditRef || null,
        }).select().single()
      if (ce) { setBusy(false); failed("Couldn't create the corrective action", ce.message); return }
      caId = ca.id
      if (logAction) logAction('Internal audit corrective action linked', 'internal_audit_findings', auditId, auditRef, { created: true })
    }
    const payload = {
      internal_audit_id: auditId, sample_id: f.sample_id || null, standard: f.standard || null, clause_ref: f.clause_ref || null,
      finding_type: f.finding_type || 'Observation', description: f.description || null, evidence: f.evidence || null,
      status: f.status || 'Open', corrective_action_id: caId, updated_at: new Date().toISOString(),
    }
    let error
    if (f.id) ({ error } = await supabase.from('internal_audit_findings').update(payload).eq('id', f.id))
    else ({ error } = await supabase.from('internal_audit_findings').insert(payload))
    setBusy(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction && caId && f.corrective_action_id && f.corrective_action_id !== '__new__')
      logAction('Internal audit corrective action linked', 'internal_audit_findings', auditId, auditRef, {})
    if (logAction) logAction(f.id ? 'Internal audit finding updated' : 'Internal audit finding created', 'internal_audit_findings', auditId, auditRef, { type: payload.finding_type })
    onSaved()
  }
  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 60 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '100%' }}>
        <h2 style={{ marginBottom: 14 }}>{f.id ? 'Edit finding' : 'Add finding'}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="field"><label>Finding type</label><select value={f.finding_type || 'Observation'} onChange={e => set('finding_type', e.target.value)}>{FINDING_TYPES.map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Status</label><select value={f.status || 'Open'} onChange={e => set('status', e.target.value)}>{['Open', 'Closed'].map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Standard</label><select value={f.standard || 'CT17'} onChange={e => set('standard', e.target.value)}>{STANDARDS.map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Clause reference</label><input value={f.clause_ref || ''} onChange={e => set('clause_ref', e.target.value)} placeholder="e.g. 9.5 / Clause 7.2" /></div>
          <div className="field"><label>Against sample (optional)</label><select value={f.sample_id || ''} onChange={e => set('sample_id', e.target.value)}>
            <option value="">— general —</option>{samples.map(s => <option key={s.id} value={s.id}>{(s.reference_label || '').slice(0, 40)}</option>)}</select></div>
          <div className="field"><label>Corrective action</label><select value={f.corrective_action_id || ''} onChange={e => set('corrective_action_id', e.target.value)}>
            <option value="">— none —</option>
            <option value="__new__">➕ Create new from this finding</option>
            {cas.map(c => <option key={c.id} value={c.id}>{(c.issue || 'CA').slice(0, 36)} ({c.status})</option>)}</select></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Description *</label><textarea rows={3} value={f.description || ''} onChange={e => set('description', e.target.value)} /></div>
          <div className="field" style={{ gridColumn: '1 / -1' }}><label>Evidence / comments</label><textarea rows={2} value={f.evidence || ''} onChange={e => set('evidence', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save finding'}</button>
          <button onClick={onClose} disabled={busy}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────── Sample picker (filter → manual tick → add) ─────────────────────────────
function SamplePicker({ existing, onClose, onAdd }) {
  const [cands, setCands] = useState([])
  const [loading, setLoading] = useState(true)
  const [sel, setSel] = useState({})
  const [f, setF] = useState({ scheme: '', from: '', to: '', status: '', client: '', auditor: '', dm: '', revised: false, complaints: false, highrisk: false })
  const existingIds = new Set((existing || []).map(s => `${s.source_table}:${s.source_id}`))

  useEffect(() => { build() }, [])
  async function build() {
    setLoading(true)
    const [{ data: jobs }, { data: decs }, { data: snaps }, { data: rack }, { data: cack }, { data: coi }, { data: comps }] = await Promise.all([
      supabase.from('jobs').select('id, scope, audit_type, audit_date, mandays, workflow_state, status, report_submitted, auditor_name, auditor2_name, office_review_by, wshc_outcome, client_id, clients(name)').order('audit_date', { ascending: false }).limit(1000),
      supabase.from('audit_decisions').select('job_id, decision_by_name, decision_outcome'),
      supabase.from('audit_report_snapshots').select('job_id, version'),
      supabase.from('report_ack_responses').select('job_id, report_version'),
      supabase.from('client_ack_responses').select('job_id'),
      supabase.from('auditor_coi_responses').select('job_id, flagged, submitted_at'),
      supabase.from('complaints').select('job_id, client, complaint_ref, record_type'),
    ])
    const decByJob = {}; (decs || []).forEach(d => { decByJob[d.job_id] = d })
    const verByJob = {}; (snaps || []).forEach(s => { verByJob[s.job_id] = Math.max(verByJob[s.job_id] || 0, s.version || 0) })
    const rackByJob = {}; (rack || []).forEach(r => { rackByJob[r.job_id] = r })
    const cackSet = new Set((cack || []).map(c => c.job_id))
    const coiByJob = {}; (coi || []).forEach(c => { const e = coiByJob[c.job_id] || { submitted: false, flagged: false }; coiByJob[c.job_id] = { submitted: e.submitted || !!c.submitted_at, flagged: e.flagged || !!c.flagged } })
    const compByJob = {}; (comps || []).forEach(c => { if (c.job_id) compByJob[c.job_id] = (compByJob[c.job_id] || 0) + 1 })
    // SAC sampling covers bizSAFE / SHMS audits only — never client Internal Audit or OTE jobs.
    const rows = (jobs || []).filter(j => !isOtherService(j.audit_type)).map(j => {
      const dec = decByJob[j.id] || {}
      const ver = verByJob[j.id] || (j.report_submitted ? 1 : 0)
      const co = coiByJob[j.id] || { submitted: false, flagged: false }
      const client = j.clients?.name || '—'
      return {
        source_table: 'jobs', source_id: j.id, client, audit_type: j.audit_type || '—', audit_date: j.audit_date,
        workflow_state: j.workflow_state || j.status || '—', auditor: j.auditor_name || '', auditor2: j.auditor2_name || '',
        decision_maker: dec.decision_by_name || '', decision_outcome: dec.decision_outcome || '', mandays: j.mandays,
        report_version: ver, report_submitted: !!j.report_submitted, office_review_by: j.office_review_by || '',
        client_ack: cackSet.has(j.id), report_ack: !!rackByJob[j.id], report_ack_version: rackByJob[j.id]?.report_version,
        coi_submitted: co.submitted, coi_flagged: co.flagged, complaints: compByJob[j.id] || 0, wshc_outcome: j.wshc_outcome || '',
        scope: j.scope || '',
        reference_label: `${client} — ${j.scope || j.audit_type || 'audit'}${j.audit_date ? ` (${fmt(j.audit_date)})` : ''}`,
      }
    })
    setCands(rows); setLoading(false)
  }

  const opts = (key) => Array.from(new Set(cands.map(c => c[key]).filter(Boolean))).sort()
  const filtered = cands.filter(c => {
    if (f.scheme && c.audit_type !== f.scheme) return false
    if (f.status && c.workflow_state !== f.status) return false
    if (f.client && c.client !== f.client) return false
    if (f.auditor && c.auditor !== f.auditor && c.auditor2 !== f.auditor) return false
    if (f.dm && c.decision_maker !== f.dm) return false
    if (f.from && c.audit_date && c.audit_date < f.from) return false
    if (f.to && c.audit_date && c.audit_date > f.to) return false
    if (f.revised && !(c.report_version > 1)) return false
    if (f.complaints && !(c.complaints > 0)) return false
    if (f.highrisk && !c.coi_flagged) return false
    return true
  })
  const toggle = (c) => setSel(s => ({ ...s, [c.source_id]: !s[c.source_id] }))
  function add() {
    const chosen = filtered.filter(c => sel[c.source_id] && !existingIds.has(`jobs:${c.source_id}`))
    if (!chosen.length) { toast('Tick at least one record', 'warning'); return }
    onAdd(chosen.map(c => ({
      source_table: 'jobs', source_id: c.source_id, reference_label: c.reference_label,
      snapshot: {
        client: c.client, scope: c.scope, audit_type: c.audit_type, audit_date: c.audit_date, mandays: c.mandays,
        auditor: c.auditor, auditor2: c.auditor2, decision_maker: c.decision_maker, decision_outcome: c.decision_outcome,
        report_version: c.report_version, report_submitted: c.report_submitted, workflow_state: c.workflow_state,
        office_review_by: c.office_review_by, wshc_outcome: c.wshc_outcome, client_ack: c.client_ack,
        report_ack: c.report_ack, report_ack_version: c.report_ack_version, coi_submitted: c.coi_submitted,
        coi_flagged: c.coi_flagged, complaints: c.complaints, captured_at: new Date().toISOString(),
      },
    })))
  }
  const selCount = filtered.filter(c => sel[c.source_id]).length

  return (
    <div className="modal-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto', zIndex: 60 }}>
      <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 920, width: '100%' }}>
        <h2 style={{ marginBottom: 4 }}>Select samples</h2>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 12 }}>Filter the records, then tick the ones to include. Nothing is auto-selected — choose as many or as few per scheme as you need.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 10 }}>
          <div className="field"><label>Scheme / audit type</label><select value={f.scheme} onChange={e => setF({ ...f, scheme: e.target.value })}><option value="">All</option>{opts('audit_type').map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Status</label><select value={f.status} onChange={e => setF({ ...f, status: e.target.value })}><option value="">All</option>{opts('workflow_state').map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Client</label><select value={f.client} onChange={e => setF({ ...f, client: e.target.value })}><option value="">All</option>{opts('client').map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Auditor</label><select value={f.auditor} onChange={e => setF({ ...f, auditor: e.target.value })}><option value="">All</option>{opts('auditor').map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Decision-maker</label><select value={f.dm} onChange={e => setF({ ...f, dm: e.target.value })}><option value="">All</option>{opts('decision_maker').map(x => <option key={x}>{x}</option>)}</select></div>
          <div className="field"><label>Audit date from</label><input type="date" value={f.from} onChange={e => setF({ ...f, from: e.target.value })} /></div>
          <div className="field"><label>Audit date to</label><input type="date" value={f.to} onChange={e => setF({ ...f, to: e.target.value })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={f.revised} onChange={e => setF({ ...f, revised: e.target.checked })} /> Returned/revised (v&gt;1)</label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={f.complaints} onChange={e => setF({ ...f, complaints: e.target.checked })} /> Has complaint</label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={f.highrisk} onChange={e => setF({ ...f, highrisk: e.target.checked })} /> Flagged COI</label>
        </div>
        <div className="table-wrap" style={{ maxHeight: 360, overflowY: 'auto' }}>
          <table>
            <thead><tr><th></th><th>Client</th><th>Scheme</th><th>Audit date</th><th>Auditor</th><th>DM</th><th>v</th><th>Flags</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 18, color: 'var(--text3)' }}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 18, color: 'var(--text3)' }}>No matching records.</td></tr>}
              {filtered.map(c => {
                const already = existingIds.has(`jobs:${c.source_id}`)
                return (
                  <tr key={c.source_id} style={{ opacity: already ? 0.5 : 1 }}>
                    <td><input type="checkbox" disabled={already} checked={!!sel[c.source_id]} onChange={() => toggle(c)} /></td>
                    <td style={{ fontSize: 13 }}>{c.client}{already ? ' (added)' : ''}</td>
                    <td style={{ fontSize: 13 }}>{c.audit_type}</td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(c.audit_date)}</td>
                    <td style={{ fontSize: 13 }}>{c.auditor || '—'}</td>
                    <td style={{ fontSize: 13 }}>{c.decision_maker || '—'}</td>
                    <td style={{ fontSize: 13, textAlign: 'center' }}>{c.report_version || '—'}</td>
                    <td style={{ fontSize: 13 }}>{[c.report_version > 1 ? 'rev' : '', c.complaints ? 'cmp' : '', c.coi_flagged ? 'COI' : ''].filter(Boolean).join(' ') || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
          <button className="primary" onClick={add}>Add {selCount || ''} selected</button>
          <button onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────── Report (print + export) ─────────────────────────────
function buildReportHtml(a, samples, findings, cas) {
  const esc = s => (s == null ? '' : String(s)).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  const cls = t => { const s = (t || '').toLowerCase(); return (s.includes('complies') || s.includes('conform')) && !s.includes('non') ? 'p-ok' : s.includes('to address') ? 'p-addr' : s.includes('observation') ? 'p-warn' : (s.includes('nonconform') || s === 'nc' || s.includes(' nc')) ? 'p-nc' : s.includes('n/a') ? 'p-na' : (s.includes('further') || s.includes('verify') || s.includes('ofi')) ? 'p-info' : 'p-na' }
  const pill = t => t ? `<span class="pill ${cls(t)}">${esc(t)}</span>` : '—'
  const sampleRows = samples.map(s => {
    const sn = s.snapshot || {}
    return `<tr><td>${esc(s.reference_label)}</td><td>${esc(sn.audit_type)}</td><td>${esc(sn.audit_date)}</td>
      <td>${esc([sn.auditor, sn.auditor2].filter(Boolean).join(', '))}</td><td>${esc(sn.decision_maker)}</td>
      <td>v${esc(sn.report_version)} ${sn.report_ack ? '· ack' : ''} ${sn.coi_flagged ? '· ⚠COI' : ''} ${sn.complaints ? '· ' + sn.complaints + ' cmp' : ''}</td>
      <td>${pill(s.conformity)}</td><td>${esc(s.sample_notes || '')}</td></tr>`
  }).join('')
  const findRows = findings.map(f => {
    const ca = cas.find(c => c.id === f.corrective_action_id)
    return `<tr><td>${pill(f.finding_type)}</td><td>${esc(f.standard)} ${esc(f.clause_ref)}</td><td>${esc(f.description)}</td>
      <td>${esc(f.evidence)}</td><td>${ca ? esc((ca.issue || 'CA').slice(0, 40)) + ' (' + esc(ca.status) + ')' : '—'}</td><td><span class="pill ${f.status === 'Closed' ? 'p-ok' : 'p-warn'}">${esc(f.status)}</span></td></tr>`
  }).join('')
  const cl = a.checklist || {}
  // Full checklist — EVERY item, grouped by section, using the saved answer where present, otherwise the
  // AuditPM/M&P pre-fill. (Previously only explicitly-saved answers rendered, so an un-saved audit exported
  // an empty checklist — the "one-page summary" problem.)
  const clResp = it => (cl[it.id]?.r || it.r || '')
  const clEvid = it => (cl[it.id]?.e ?? it.e ?? '')
  const clAnsweredCount = IA_CHECKLIST.filter(it => clResp(it)).length
  const clRows = Object.entries(IA_SECTIONS).map(([k, label]) => {
    const items = IA_CHECKLIST.filter(it => it.s === k)
    if (!items.length) return ''
    const hdr = `<tr><td colspan="4" style="background:#eef2f8;color:#1F3864;font-weight:600">${esc(label)}</td></tr>`
    return hdr + items.map(it => `<tr><td style="white-space:nowrap">${esc(it.cl)}</td><td>${esc(it.q)}</td><td>${pill(clResp(it))}</td><td>${esc(clEvid(it))}</td></tr>`).join('')
  }).join('')
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(a.ref)} Internal Audit Report</title>
    <style>
    :root{--navy:#1F3864;--blue:#2E5496;--ink:#1f2937;--muted:#6b7280;--line:#e5e9f0}
    *{box-sizing:border-box}
    body{font-family:'Segoe UI',Calibri,Arial,sans-serif;color:var(--ink);margin:0;padding:30px 34px;font-size:12.5px;line-height:1.5}
    .band{border-bottom:3px solid var(--blue);padding-bottom:12px;margin-bottom:14px}
    .firm{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--blue);font-weight:700}
    h1{font-size:22px;margin:5px 0 2px;color:var(--navy)}
    .sub{color:var(--muted);font-size:13px}
    h2{font-size:13px;color:var(--navy);margin:22px 0 8px;padding-left:9px;border-left:3px solid var(--blue)}
    .meta{width:100%;border-collapse:collapse;margin-top:6px}
    .meta td{border:none;padding:4px 8px 4px 0;font-size:12px;vertical-align:top}
    .meta td.k{color:var(--muted);font-weight:600;white-space:nowrap;width:1%;padding-right:12px}
    .tiles{width:100%;border-collapse:separate;border-spacing:8px 0;margin:14px 0 2px;table-layout:fixed}
    .tile{border:1px solid var(--line);border-radius:10px;padding:10px 13px;background:#fafbfd;text-align:left;vertical-align:top}
    .tile .n{font-size:20px;font-weight:700;color:var(--navy)}
    .tile .l{font-size:10px;color:var(--muted);text-transform:uppercase;letter-spacing:.05em}
    table{width:100%;border-collapse:collapse;margin-top:4px}
    th,td{border:1px solid var(--line);padding:6px 8px;text-align:left;vertical-align:top;font-size:11px}
    th{background:#f0f4fa;color:var(--navy);font-weight:600}
    tbody tr:nth-child(even){background:#fafbfd}
    .pill{display:inline-block;padding:1px 8px;border-radius:20px;font-size:10.5px;font-weight:600;white-space:nowrap}
    .p-ok{background:#EAF3DE;color:#27500a}.p-warn{background:#FFF3E0;color:#854F0B}
    .p-nc{background:#FDECEA;color:#791F1F}.p-na{background:#eef0f2;color:#555}.p-info{background:#EDE7F6;color:#4527A0}.p-addr{background:#E7ECF6;color:#1F3864}
    .foot{margin-top:26px;border-top:1px solid var(--line);padding-top:8px;color:var(--muted);font-size:10.5px;display:flex;justify-content:space-between}
    </style></head><body>
    <div class="band"><div class="firm">CCIS Singapore Pte Ltd · Auditing Organisation</div>
      <h1>Internal Audit Report</h1><div class="sub">${esc(a.ref)}${a.title ? ' — ' + esc(a.title) : ''}</div></div>
    <table class="tiles"><tr>
      <td class="tile"><div class="n">${samples.length}</div><div class="l">Samples</div></td>
      <td class="tile"><div class="n">${findings.length}</div><div class="l">Findings</div></td>
      <td class="tile"><div class="n">${findings.filter(f => (f.finding_type || '').includes('NC')).length}</div><div class="l">Nonconformities</div></td>
      <td class="tile"><div class="n">${clAnsweredCount}/${IA_CHECKLIST.length}</div><div class="l">Checklist answered</div></td>
    </tr></table>
    <h2>Audit details</h2>
    <table class="meta">
      <tr><td class="k">Objective</td><td>${esc(a.objective) || '—'}</td><td class="k">Lead internal auditor</td><td>${esc(a.lead_internal_auditor) || '—'}</td></tr>
      <tr><td class="k">Scope</td><td>${esc(a.scope) || '—'}</td><td class="k">Criteria</td><td>${esc(a.criteria) || '—'}</td></tr>
      <tr><td class="k">Audit period</td><td>${esc(a.period_start) || '—'} to ${esc(a.period_end) || '—'}</td><td class="k">Planned / conducted</td><td>${esc(a.planned_date) || '—'} / ${esc(a.conducted_date) || '—'}</td></tr>
      <tr><td class="k">Status</td><td>${esc(a.status) || '—'}</td><td class="k"></td><td></td></tr>
    </table>
    <h2>Selected samples (${samples.length})</h2>
    <table><thead><tr><th>Reference</th><th>Scheme</th><th>Audit date</th><th>Auditor(s)</th><th>Report approver</th><th>Evidence facts</th><th>Conformity</th><th>Notes</th></tr></thead>
    <tbody>${sampleRows || '<tr><td colspan="8">No samples.</td></tr>'}</tbody></table>
    <h2>Findings, observations &amp; OFIs (${findings.length})</h2>
    <table><thead><tr><th>Type</th><th>Standard / clause</th><th>Description</th><th>Evidence</th><th>Corrective action</th><th>Status</th></tr></thead>
    <tbody>${findRows || '<tr><td colspan="6">No findings.</td></tr>'}</tbody></table>
    <h2>Checklist — SAC / CT 17 (${clAnsweredCount} of ${IA_CHECKLIST.length} answered)</h2>
    <table><thead><tr><th>Clause</th><th>Requirement</th><th>Response</th><th>Evidence</th></tr></thead>
    <tbody>${clRows || '<tr><td colspan="4">Checklist not completed.</td></tr>'}</tbody></table>
    <h2>Conclusion</h2><div>${esc(a.conclusion) || '—'}</div>
    <div class="foot"><span>CCIS Internal Audit Report · ${esc(a.ref)}</span><span>Generated ${new Date().toLocaleDateString('en-SG')} · Uncontrolled when printed unless verified against AuditPM</span></div>
    </body></html>`
  return html
}
function printReport(a, samples, findings, cas) {
  const w = window.open('', '_blank')
  if (!w) { toast('Allow pop-ups to print or save the PDF', 'warning'); return }
  w.document.write(buildReportHtml(a, samples, findings, cas)); w.document.close(); w.focus(); setTimeout(() => w.print(), 300)
}
function exportReport(a, samples, findings, cas) {
  const blob = new Blob(['﻿' + buildReportHtml(a, samples, findings, cas)], { type: 'application/msword' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${(a.ref || 'internal-audit').replace(/[^\w-]+/g, '_')}_Internal_Audit_Report.doc`
  document.body.appendChild(link); link.click(); document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
